import AnalyticCard from '@/components/Analytic/Card'
import EChartCustomized, {
    SCHEMES,
    TOOLTIP_OPTIONS,
} from '@/components/EchartCustomized'
import AnalyticsIcons from '@/icons/AnalyticsIcons'
import AnalyticsUpIcons from '@/icons/AnalyticsUpIcons'
import ChartIcons from '@/icons/ChartIcons'
import dayjs from 'dayjs'
import React from 'react'
import { Typogaphy } from 'vnetwork-ui'

// New margarita-backed analytics envelope (data.* from GET /services/:id/analytics).
// Backend returns a single metric per `type` as a flat time series.
interface SeriesPoint {
    timestamp: string
    value: number
}
export interface AnalyticSeriesData {
    series?: SeriesPoint[]
    total?: number
    metric?: string
}

interface AnalyticSeriesChartProps {
    data?: AnalyticSeriesData
    title: string
    // Display unit for tooltip/summary (e.g. 'hours', 'GB', 'jobs').
    unit: string
    // Accent color for the line/bar + peak marker (mirrors CDN per-chart accent).
    color: string
    variant?: 'area' | 'bar'
    emptyText: string
    // Label under the headline total stat (defaults to "Total <unit>").
    totalLabel?: string
    // Hide the Total/Peak/Avg stat cards (e.g. jobs — the chart alone is enough).
    hideStats?: boolean
}

const round2 = (n: number) => Math.round((n || 0) * 100) / 100
const fmt = (n: number) => (n || 0).toLocaleString('en-US')

const AnalyticSeriesChart: React.FC<AnalyticSeriesChartProps> = ({
    data,
    title,
    unit,
    color,
    variant = 'area',
    emptyText,
    totalLabel,
    hideStats,
}) => {
    const points = (data?.series ?? []).map((point) => ({
        timestamp: dayjs(point.timestamp).format('YYYY-MM-DD'),
        value: round2(point.value),
    }))

    const total = round2(data?.total ?? 0)
    const values = points.map((p) => p.value)
    const peak = values.length ? Math.max(...values) : 0
    const peakItem = points.find((p) => p.value === peak)
    const avg = values.length
        ? round2(values.reduce((s, v) => s + v, 0) / values.length)
        : 0

    const timestamps = points.map((p) => p.timestamp)

    // Shared echarts config mirrors CDN VolumeChart/BandwidthChart: dashed axis,
    // #e0e0e0 dashed splitLine, TOOLTIP_OPTIONS, pin markPoint for the peak.
    const options = {
        tooltip: {
            trigger: 'axis',
            ...TOOLTIP_OPTIONS,
            formatter: (params: any) => {
                const [current] = params
                const v = current?.value
                return `<b>${dayjs(current?.name)?.format('DD MMM YYYY')}</b> ${
                    v || v === 0 ? `${fmt(v)} ${unit}` : '-'
                }<br/>`
            },
        },
        grid: {
            top: '15%',
            left: 8,
            right: '2%',
            bottom: '0%',
            containLabel: true,
        },
        xAxis: {
            type: 'category',
            boundaryGap: variant === 'bar',
            data: timestamps,
            axisLine: {
                lineStyle: {
                    type: [3, 3],
                    dashOffset: 3,
                },
            },
            axisLabel: {
                align: 'left',
                formatter: (value: any) => dayjs(value)?.format('DD MMM'),
            },
        },
        yAxis: {
            type: 'value',
            splitNumber: 2,
            splitLine: {
                show: true,
                lineStyle: {
                    color: '#e0e0e0',
                    type: [3, 3],
                    dashOffset: 3,
                },
            },
            axisLabel: {
                align: 'right',
                margin: 8,
                color: 'black',
                formatter: (value: number) => fmt(value),
            },
        },
        color: [color],
        series: [
            {
                data: values,
                type: variant === 'bar' ? 'bar' : 'line',
                animation: false,
                showSymbol: false,
                barMaxWidth: 28,
                itemStyle:
                    variant === 'bar'
                        ? { color, borderRadius: [4, 4, 0, 0] }
                        : undefined,
                // CDN wash: neutral light→white gradient under the line.
                areaStyle:
                    variant === 'bar' ? undefined : { color: SCHEMES.gradient },
                markPoint: {
                    symbol: 'pin',
                    symbolSize: 60,
                    data: [
                        {
                            type: 'max',
                            name: 'Peak',
                            itemStyle: { color },
                            label: {
                                formatter: (param: any) => fmt(param?.value),
                                color: '#ffffff',
                                fontWeight: 'bold',
                                fontSize: 12,
                                padding: [4, 6],
                            },
                        },
                    ],
                },
            },
        ],
    }

    return (
        <div className="space-y-2">
            {points.length > 0 && !hideStats && (
                <div className="grid xs:grid-cols-1 md:grid-cols-3 gap-2">
                    <AnalyticCard
                        title={`${fmt(total)} ${unit}`}
                        subtitle={totalLabel ?? `Total ${unit}`}
                        icon={<AnalyticsIcons />}
                        text={[
                            {
                                prefix: `Peak ${unit}`,
                                suffix: fmt(peak),
                            },
                        ]}
                    />
                    <AnalyticCard
                        title={`${fmt(peak)} ${unit}`}
                        subtitle={`Peak ${unit}`}
                        icon={<ChartIcons />}
                        text={[
                            {
                                prefix: 'Date',
                                suffix: peakItem
                                    ? dayjs(peakItem.timestamp).format('DD MMM')
                                    : '-',
                            },
                        ]}
                    />
                    <AnalyticCard
                        title={`${fmt(avg)} ${unit}`}
                        subtitle={`Avg ${unit}/point`}
                        icon={<AnalyticsUpIcons />}
                        text={[
                            {
                                prefix: 'Data points',
                                suffix: fmt(points.length),
                            },
                        ]}
                    />
                </div>
            )}

            <div className="bg-white rounded-[8px] p-4">
                <div>
                    <Typogaphy
                        size="medium"
                        className="font-semibold text-primary"
                    >
                        {title}
                    </Typogaphy>
                </div>

                {points.length > 0 ? (
                    <>
                        <EChartCustomized
                            options={options as any}
                            style={{ height: 320 }}
                        />
                        <div className="py-4">
                            <div className="flex items-center gap-2">
                                <div
                                    className="inline-block w-[10px] h-[10px]"
                                    style={{ backgroundColor: color }}
                                />
                                <Typogaphy>{title}</Typogaphy>
                            </div>
                        </div>
                        <div className="text-center">
                            <div className="border-1 bg-gray-100 px-2 py-1 inline-block">
                                <div className="flex gap-1">
                                    <Typogaphy
                                        size="small"
                                        className="text-gray-500"
                                    >
                                        {totalLabel ?? `Total ${unit}`}:
                                    </Typogaphy>
                                    <Typogaphy
                                        size="small"
                                        className="font-medium"
                                    >
                                        {fmt(total)} {unit}
                                    </Typogaphy>
                                </div>
                            </div>
                        </div>
                    </>
                ) : (
                    <div className="h-80 flex items-center justify-center">
                        <Typogaphy className="text-gray-500">
                            {emptyText}
                        </Typogaphy>
                    </div>
                )}
            </div>
        </div>
    )
}

export default AnalyticSeriesChart
